import React, { useContext, useEffect, useState } from 'react';
import {
    Box,
    Typography,
    TextField,
    Button,
    MenuItem,
    Select,
    FormControl,
    InputLabel,
    CircularProgress,
    Alert,
    Paper,
    Stack,
    Tabs,
    Tab,
    Divider,
    Grid,
    Switch,
    FormControlLabel,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
} from '@mui/material';
import { useAuth } from '../auth';
import { baseBackendUrl } from '../service/baseService';

interface MailingListMember {
    id: number;
    email: string;
    guid: string;
    isUnsubscribed: boolean;
    jhiGroup: string;
    zip: string;
    address: string;
    city: string;
    state: string;
    phoneNumber: string;
    fullName: string;
    template: string;
}

const ListManagement: React.FC = () => {
    const { IsAdmin, cookies } = useContext(useAuth);
    const [tabValue, setTabValue] = useState(0);
    const [mailingLists, setMailingLists] = useState<string[]>([]);
    const [selectedList, setSelectedList] = useState<string>('');
    const [members, setMembers] = useState<MailingListMember[]>([]);
    const [selectedMember, setSelectedMember] = useState<MailingListMember | null>(null);
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<string>('');
    const [success, setSuccess] = useState<string>('');

    // New List State
    const [newListName, setNewListName] = useState('');
    const [newMember, setNewMember] = useState<Partial<MailingListMember>>({
        email: '',
        fullName: '',
        isUnsubscribed: false,
        jhiGroup: '',
        zip: '',
        address: '',
        city: '',
        state: '',
        phoneNumber: '',
        template: ''
    });

    // Delete Dialog State
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [listToDelete, setListToDelete] = useState('');

    // Add Individual State
    const [isAddingMember, setIsAddingMember] = useState(false);
    const [addMemberData, setAddMemberData] = useState<Partial<MailingListMember>>({
        email: '',
        fullName: '',
        isUnsubscribed: false,
        zip: '',
        address: '',
        city: '',
        state: '',
        phoneNumber: '',
        template: ''
    });


    useEffect(() => {
        fetchMailingLists();
    }, [cookies]);

    const fetchMailingLists = async () => {
        if (!cookies?.['UserAuth']?.token) return;
        try {
            const response = await fetch(`${baseBackendUrl}/api/email/Get-Available-Mailing-Lists`, {
                headers: { 'Authorization': `Bearer ${cookies['UserAuth'].token}` }
            });
            if (!response.ok) throw new Error('Failed to fetch mailing lists');
            const data = await response.json();
            setMailingLists(data);
        } catch (err) {
            setError('Failed to load mailing lists');
        }
    };

    const fetchMembers = async (listName: string) => {
        if (!cookies?.['UserAuth']?.token || !listName) return;
        setLoading(true);
        try {
            const response = await fetch(`${baseBackendUrl}/api/email/get-all-members?mailingListName=${encodeURIComponent(listName)}`, {
                headers: { 'Authorization': `Bearer ${cookies['UserAuth'].token}` }
            });
            if (!response.ok) throw new Error('Failed to fetch members');
            const data = await response.json();
            setMembers(data);
            setSelectedMember(null);
            setIsAddingMember(false);
        } catch (err) {
            setError('Failed to load members');
        } finally {
            setLoading(false);
        }
    };

    const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
        setTabValue(newValue);
        setError('');
        setSuccess('');
    };

    const handleListChange = (listName: string) => {
        setSelectedList(listName);
        fetchMembers(listName);
    };

    const handleMemberUpdate = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!selectedMember || !cookies?.['UserAuth']?.token) return;
        setLoading(true);
        try {
            const response = await fetch(`${baseBackendUrl}/api/email/update-member`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${cookies['UserAuth'].token}`
                },
                body: JSON.stringify(selectedMember),
            });
            if (!response.ok) throw new Error('Failed to update member');
            setSuccess('Member updated successfully');
            fetchMembers(selectedList);
        } catch (err) {
            setError('Failed to update member');
        } finally {
            setLoading(false);
        }
    };

    const handleAddIndividual = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!selectedList || !addMemberData.email || !cookies?.['UserAuth']?.token) return;
        setLoading(true);
        try {
            const memberToAdd = { ...addMemberData, jhiGroup: selectedList };
            const response = await fetch(`${baseBackendUrl}/api/email/add-member`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${cookies['UserAuth'].token}`
                },
                body: JSON.stringify(memberToAdd),
            });
            if (!response.ok) throw new Error('Failed to add member');
            setSuccess('Member added to list successfully');
            setIsAddingMember(false);
            setAddMemberData({
                email: '',
                fullName: '',
                isUnsubscribed: false,
                zip: '',
                address: '',
                city: '',
                state: '',
                phoneNumber: '',
                template: ''
            });
            fetchMembers(selectedList);
        } catch (err) {
            setError('Failed to add member');
        } finally {
            setLoading(false);
        }
    };

    const handleCreateList = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!newListName || !newMember.email || !cookies?.['UserAuth']?.token) return;
        setLoading(true);
        try {
            const memberToAdd = { ...newMember, jhiGroup: newListName };
            const response = await fetch(`${baseBackendUrl}/api/email/add-member`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${cookies['UserAuth'].token}`
                },
                body: JSON.stringify(memberToAdd),
            });
            if (!response.ok) throw new Error('Failed to create list');
            setSuccess(`Mailing list "${newListName}" created with first member.`);
            setNewListName('');
            setNewMember({ email: '', fullName: '' });
            fetchMailingLists();
        } catch (err) {
            setError('Failed to create list');
        } finally {
            setLoading(false);
        }
    };

    const handleDeleteList = async () => {
        if (!listToDelete || !cookies?.['UserAuth']?.token) return;
        setLoading(true);
        try {
            const response = await fetch(`${baseBackendUrl}/api/email/delete-list?mailingListName=${encodeURIComponent(listToDelete)}`, {
                method: 'DELETE',
                headers: { 'Authorization': `Bearer ${cookies['UserAuth'].token}` }
            });
            if (!response.ok) throw new Error('Failed to delete list');
            setSuccess(`Mailing list "${listToDelete}" and all its members deleted.`);
            setListToDelete('');
            fetchMailingLists();
            if (selectedList === listToDelete) {
                setSelectedList('');
                setMembers([]);
            }
        } catch (err) {
            setError('Failed to delete list');
        } finally {
            setLoading(false);
            setDeleteDialogOpen(false);
        }
    };

    if (!IsAdmin) {
        return (
            <Box sx={{ p: 3, pt: 10, textAlign: 'center' }}>
                <Typography variant="h5" color="error">Access Denied</Typography>
            </Box>
        );
    }

    return (
        <Box sx={{ p: 3, pt: 10, maxWidth: 1200, margin: '0 auto' }}>
            <Typography variant="h4" gutterBottom>List Management</Typography>

            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
            {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

            <Paper sx={{ width: '100%', mb: 3 }}>
                <Tabs value={tabValue} onChange={handleTabChange} centered>
                    <Tab label="Manage Individuals" />
                    <Tab label="Manage Mailing Lists" />
                </Tabs>
            </Paper>

            {tabValue === 0 && (
                <Box>
                    <Grid container spacing={3}>
                        <Grid item xs={12} md={4}>
                            <Paper sx={{ p: 2 }}>
                                <FormControl fullWidth sx={{ mb: 2 }}>
                                    <InputLabel>Select Mailing List</InputLabel>
                                    <Select
                                        value={selectedList}
                                        label="Select Mailing List"
                                        onChange={(e) => handleListChange(e.target.value)}
                                    >
                                        {mailingLists.map((list) => (
                                            <MenuItem key={list} value={list}>{list}</MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>

                                {selectedList && (
                                    <Box sx={{ mt: 2 }}>
                                        <FormControl fullWidth sx={{ mb: 2 }}>
                                            <InputLabel>Select Individual</InputLabel>
                                            <Select
                                                value={selectedMember?.id || ''}
                                                label="Select Individual"
                                                onChange={(e) => {
                                                    const m = members.find(member => member.id === e.target.value);
                                                    setSelectedMember(m ? { ...m } : null);
                                                    setIsAddingMember(false);
                                                }}
                                            >
                                                {members.map((m) => (
                                                    <MenuItem key={m.id} value={m.id}>
                                                        {m.fullName || m.email} {m.isUnsubscribed ? '(Unsubscribed)' : ''}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                        <Button
                                            fullWidth
                                            variant="outlined"
                                            onClick={() => {
                                                setIsAddingMember(true);
                                                setSelectedMember(null);
                                            }}
                                            disabled={isAddingMember}
                                        >
                                            Add New Member to this List
                                        </Button>
                                    </Box>
                                )}
                            </Paper>
                        </Grid>

                        <Grid item xs={12} md={8}>
                            {selectedMember && !isAddingMember ? (
                                <Paper sx={{ p: 3 }}>
                                    <Typography variant="h6" gutterBottom>Edit Recipient</Typography>
                                    <form onSubmit={handleMemberUpdate}>
                                        <Grid container spacing={2}>
                                            <Grid item xs={12} sm={6}>
                                                <TextField
                                                    fullWidth label="Full Name"
                                                    value={selectedMember.fullName || ''}
                                                    onChange={(e) => setSelectedMember({ ...selectedMember, fullName: e.target.value })}
                                                />
                                            </Grid>
                                            <Grid item xs={12} sm={6}>
                                                <TextField
                                                    fullWidth label="Email"
                                                    value={selectedMember.email || ''}
                                                    onChange={(e) => setSelectedMember({ ...selectedMember, email: e.target.value })}
                                                />
                                            </Grid>
                                            <Grid item xs={12} sm={6}>
                                                <TextField
                                                    fullWidth label="Phone Number"
                                                    value={selectedMember.phoneNumber || ''}
                                                    onChange={(e) => setSelectedMember({ ...selectedMember, phoneNumber: e.target.value })}
                                                />
                                            </Grid>
                                            <Grid item xs={12} sm={6}>
                                                <TextField
                                                    fullWidth label="Zip Code"
                                                    value={selectedMember.zip || ''}
                                                    onChange={(e) => setSelectedMember({ ...selectedMember, zip: e.target.value })}
                                                />
                                            </Grid>
                                            <Grid item xs={12}>
                                                <TextField
                                                    fullWidth label="Address"
                                                    value={selectedMember.address || ''}
                                                    onChange={(e) => setSelectedMember({ ...selectedMember, address: e.target.value })}
                                                />
                                            </Grid>
                                            <Grid item xs={12} sm={6}>
                                                <TextField
                                                    fullWidth label="City"
                                                    value={selectedMember.city || ''}
                                                    onChange={(e) => setSelectedMember({ ...selectedMember, city: e.target.value })}
                                                />
                                            </Grid>
                                            <Grid item xs={12} sm={6}>
                                                <TextField
                                                    fullWidth label="State"
                                                    value={selectedMember.state || ''}
                                                    onChange={(e) => setSelectedMember({ ...selectedMember, state: e.target.value })}
                                                />
                                            </Grid>
                                            <Grid item xs={12} sm={6}>
                                                <TextField
                                                    fullWidth label="Template"
                                                    value={selectedMember.template || ''}
                                                    onChange={(e) => setSelectedMember({ ...selectedMember, template: e.target.value })}
                                                />
                                            </Grid>
                                            <Grid item xs={12} sm={6}>
                                                <FormControlLabel
                                                    control={
                                                        <Switch
                                                            checked={selectedMember.isUnsubscribed}
                                                            onChange={(e) => setSelectedMember({ ...selectedMember, isUnsubscribed: e.target.checked })}
                                                        />
                                                    }
                                                    label="Unsubscribed"
                                                />
                                            </Grid>
                                        </Grid>
                                        <Button
                                            type="submit"
                                            variant="contained"
                                            sx={{ mt: 3 }}
                                            disabled={loading}
                                        >
                                            {loading ? <CircularProgress size={24} /> : 'Save Changes'}
                                        </Button>
                                    </form>
                                </Paper>
                            ) : isAddingMember ? (
                                <Paper sx={{ p: 3 }}>
                                    <Typography variant="h6" gutterBottom>Add Member to "{selectedList}"</Typography>
                                    <form onSubmit={handleAddIndividual}>
                                        <Grid container spacing={2}>
                                            <Grid item xs={12} sm={6}>
                                                <TextField
                                                    fullWidth label="Full Name"
                                                    value={addMemberData.fullName || ''}
                                                    onChange={(e) => setAddMemberData({ ...addMemberData, fullName: e.target.value })}
                                                    required
                                                />
                                            </Grid>
                                            <Grid item xs={12} sm={6}>
                                                <TextField
                                                    fullWidth label="Email"
                                                    type="email"
                                                    value={addMemberData.email || ''}
                                                    onChange={(e) => setAddMemberData({ ...addMemberData, email: e.target.value })}
                                                    required
                                                />
                                            </Grid>
                                            <Grid item xs={12} sm={6}>
                                                <TextField
                                                    fullWidth label="Phone Number"
                                                    value={addMemberData.phoneNumber || ''}
                                                    onChange={(e) => setAddMemberData({ ...addMemberData, phoneNumber: e.target.value })}
                                                />
                                            </Grid>
                                            <Grid item xs={12} sm={6}>
                                                <TextField
                                                    fullWidth label="Zip Code"
                                                    value={addMemberData.zip || ''}
                                                    onChange={(e) => setAddMemberData({ ...addMemberData, zip: e.target.value })}
                                                />
                                            </Grid>
                                            <Grid item xs={12}>
                                                <TextField
                                                    fullWidth label="Address"
                                                    value={addMemberData.address || ''}
                                                    onChange={(e) => setAddMemberData({ ...addMemberData, address: e.target.value })}
                                                />
                                            </Grid>
                                            <Grid item xs={12} sm={6}>
                                                <TextField
                                                    fullWidth label="City"
                                                    value={addMemberData.city || ''}
                                                    onChange={(e) => setAddMemberData({ ...addMemberData, city: e.target.value })}
                                                />
                                            </Grid>
                                            <Grid item xs={12} sm={6}>
                                                <TextField
                                                    fullWidth label="State"
                                                    value={addMemberData.state || ''}
                                                    onChange={(e) => setAddMemberData({ ...addMemberData, state: e.target.value })}
                                                />
                                            </Grid>
                                            <Grid item xs={12} sm={6}>
                                                <TextField
                                                    fullWidth label="Template"
                                                    value={addMemberData.template || ''}
                                                    onChange={(e) => setAddMemberData({ ...addMemberData, template: e.target.value })}
                                                />
                                            </Grid>
                                        </Grid>
                                        <Stack direction="row" spacing={2} sx={{ mt: 3 }}>
                                            <Button
                                                type="submit"
                                                variant="contained"
                                                disabled={loading}
                                            >
                                                {loading ? <CircularProgress size={24} /> : 'Add Member'}
                                            </Button>
                                            <Button
                                                variant="outlined"
                                                onClick={() => setIsAddingMember(false)}
                                                disabled={loading}
                                            >
                                                Cancel
                                            </Button>
                                        </Stack>
                                    </form>
                                </Paper>
                            ) : (
                                <Paper sx={{ p: 3, textAlign: 'center' }}>
                                    <Typography color="text.secondary">Select an individual to edit their details, or add a new one.</Typography>
                                </Paper>
                            )}
                        </Grid>
                    </Grid>
                </Box>
            )}

            {tabValue === 1 && (
                <Box>
                    <Grid container spacing={3}>
                        <Grid item xs={12} md={6}>
                            <Paper sx={{ p: 3 }}>
                                <Typography variant="h6" gutterBottom>Add New Mailing List</Typography>
                                <form onSubmit={handleCreateList}>
                                    <TextField
                                        fullWidth label="Mailing List Name"
                                        value={newListName}
                                        onChange={(e) => setNewListName(e.target.value)}
                                        sx={{ mb: 2 }} required
                                    />
                                    <Divider sx={{ my: 2 }} />
                                    <Typography variant="subtitle2" gutterBottom>First Member Details</Typography>
                                    <TextField
                                        fullWidth label="Full Name"
                                        value={newMember.fullName}
                                        onChange={(e) => setNewMember({ ...newMember, fullName: e.target.value })}
                                        sx={{ mb: 2 }} required
                                    />
                                    <TextField
                                        fullWidth label="Email"
                                        type="email"
                                        value={newMember.email}
                                        onChange={(e) => setNewMember({ ...newMember, email: e.target.value })}
                                        sx={{ mb: 2 }} required
                                    />
                                    <Button
                                        type="submit"
                                        variant="contained"
                                        disabled={loading}
                                        fullWidth
                                    >
                                        {loading ? <CircularProgress size={24} /> : 'Create List'}
                                    </Button>
                                </form>
                            </Paper>
                        </Grid>

                        <Grid item xs={12} md={6}>
                            <Paper sx={{ p: 3 }}>
                                <Typography variant="h6" gutterBottom color="error">Delete Mailing List</Typography>
                                <Typography variant="body2" color="text.secondary" paragraph>
                                    Warning: This will delete the list and ALL recipients associated with it. This action cannot be undone.
                                </Typography>
                                <FormControl fullWidth sx={{ mb: 2 }}>
                                    <InputLabel>Select List to Delete</InputLabel>
                                    <Select
                                        value={listToDelete}
                                        label="Select List to Delete"
                                        onChange={(e) => setListToDelete(e.target.value)}
                                    >
                                        {mailingLists.map((list) => (
                                            <MenuItem key={list} value={list}>{list}</MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                                <Button
                                    variant="contained"
                                    color="error"
                                    disabled={!listToDelete || loading}
                                    fullWidth
                                    onClick={() => setDeleteDialogOpen(true)}
                                >
                                    Delete Entire List
                                </Button>
                            </Paper>
                        </Grid>
                    </Grid>
                </Box>
            )}

            <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
                <DialogTitle>Confirm Deletion</DialogTitle>
                <DialogContent>
                    <Typography>
                        Are you sure you want to delete the mailing list <strong>"{listToDelete}"</strong> and all its members?
                        This action is permanent and cannot be reversed.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
                    <Button onClick={handleDeleteList} color="error" variant="contained">
                        Yes, Delete Everything
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default ListManagement;
